import type { CreatePushSubscriptionParams, PushSubscriptionRow } from 'services/api/pushSubscriptions';

// Browser-side Web Push helpers (OLIO-3563, PWA Push POC). Plain functions, no React, so the hook
// and the tests share one implementation.

export const SERVICE_WORKER_URL = '/sw.js';

export type PushPermission = NotificationPermission | 'unsupported';

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** iOS only delivers web push to a web app opened from the Home Screen; this is how the page tells. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.('(display-mode: standalone)')?.matches === true || nav.standalone === true;
}

// iPadOS Safari sends a desktop Mac user agent by default, so a touch-screen "Mac" is an iPad.
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  );
}

export function notificationPermission(): PushPermission {
  return isPushSupported() ? Notification.permission : 'unsupported';
}

/** The browser's current subscription, if this origin has a registered worker holding one. A read:
 * it never registers the worker — no subscription can exist without one, and registering on every
 * page load (for every flagged user, in every frame) is the enable path's job, not a lookup's. */
export async function getBrowserSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

export async function subscribeBrowser(vapidPublicKey: string): Promise<PushSubscription> {
  const registration =
    (await navigator.serviceWorker.getRegistration('/')) ??
    (await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' }));
  // A just-registered worker has no active instance yet, and subscribe() needs one.
  await navigator.serviceWorker.ready;
  // The API serves the key already in the form the Push API takes as a string: base64url, no padding.
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidPublicKey });
}

export function subscriptionToParams(subscription: PushSubscription): CreatePushSubscriptionParams {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('The browser returned an incomplete push subscription.');
  }
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    userAgent: navigator.userAgent,
  };
}

/** A short human name for a device row's user agent — "Chrome on Mac", "Safari on iPhone". iOS
 * browsers ship their own tokens (CriOS, FxiOS, EdgiOS) with no "Chrome/", so they come first.
 * Known limit: iPadOS Safari sends a desktop Mac user agent and reads as "Safari on Mac". */
export function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device';
  const browser = /EdgiOS\/|Edg\//.test(userAgent)
    ? 'Edge'
    : /CriOS\/|Chrome\//.test(userAgent)
      ? 'Chrome'
      : /FxiOS\/|Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Safari\//.test(userAgent)
          ? 'Safari'
          : 'Browser';
  const platform = /iPhone/.test(userAgent)
    ? 'iPhone'
    : /iPad/.test(userAgent)
      ? 'iPad'
      : /Android/.test(userAgent)
        ? 'Android'
        : /Macintosh/.test(userAgent)
          ? 'Mac'
          : /Windows/.test(userAgent)
            ? 'Windows'
            : /Linux/.test(userAgent)
              ? 'Linux'
              : 'unknown platform';
  return `${browser} on ${platform}`;
}

/** Whether a device can be reached. A row the push service dropped can't be, and neither can this
 * browser while it is blocked in its own settings: the server can't see that block, so its row still
 * looks live. The Devices section's status and the save-time "no devices" check share this rule. */
export function isReceiving(
  device: PushSubscriptionRow,
  { isThisDevice, permission }: { isThisDevice: boolean; permission: PushPermission }
): boolean {
  return !device.failedAt && !(isThisDevice && permission === 'denied');
}
